import React, { useEffect, useRef } from 'react';
import { useI18n } from '../i18n';

const PropertiesTab = ({ parcels, onParcelSelect, selectedParcel }) => {
  const sectionsRef = useRef([]);

  const imageUrl = (fileName) => `${process.env.PUBLIC_URL}/Images/${encodeURIComponent(fileName)}`;

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    // Fallback: if IntersectionObserver doesn't trigger (e.g. hidden container),
    // ensure elements are visible by adding the animation class on mount.
    // This prevents the properties list from remaining invisible.
    sectionsRef.current.forEach((section) => {
      if (section && !section.classList.contains('animate-in')) {
        section.classList.add('animate-in');
      }
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  const { t } = useI18n();
  return (
    <div className="properties-page">
      {/* Hero Section */}
      <section className="properties-hero" ref={addToRefs} style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url('${imageUrl('FOTO DRONE 09.JPG')}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'scroll'
      }}>
        <div className="properties-hero-content">
          <h1 className="properties-hero-title">{t('properties_hero_title') || 'Available Land Parcels'}</h1>
          <p className="properties-hero-subtitle">{t('properties_hero_subtitle') || 'Explore our premium selection of land parcels, each offering unique opportunities for industrial, residential, or commercial development.'}</p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="unified-stats-section" ref={addToRefs}>
        <div className="unified-stats-container">
          <div className="unified-stats-grid">
            <div className="unified-stat-card">
              <span className="unified-stat-number">21</span>
              <span className="unified-stat-label">{t('properties_stat_available') || 'available parcels'}</span>
            </div>
            <div className="unified-stat-card">
              <span className="unified-stat-number">200+</span>
              <span className="unified-stat-label">{t('properties_stat_hectares_sale') || 'hectares available for sale'}</span>
            </div>
            <div className="unified-stat-card">
              <span className="unified-stat-number">90+</span>
              <span className="unified-stat-label">{t('properties_stat_hectares_util') || 'hectares with utility access'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Properties List Section */}
      <section className="properties-listing" ref={addToRefs}>
        <div className="properties-content">
          <div className="properties-list">
            {parcels.map((parcel, index) => {
              const isSold = parcel.status === 'sold' || parcel.sold === true;
              return (
                <div 
                  key={parcel.id} 
                  className={`property-card ${index % 2 === 0 ? 'card-left' : 'card-right'}${isSold ? ' sold' : ''}`}
                  ref={addToRefs}
                >
                  <div className="property-image">
                    <div className={`property-status-badge ${isSold ? 'status-sold' : 'status-available'}`}>
                      {isSold ? (t('properties_sold') || 'Sold') : (t('properties_available') || 'Available for sale')}
                    </div>
                    <img 
                      src={parcel.image || `https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80`}
                      alt={parcel.name}
                    />
                  </div>
                  <div className="property-content">
                    <div className="property-header">
                      <h3>{parcel.name}</h3>
                      <div className="property-price">{isSold ? (t('properties_not_available') || 'Not available for sale') : (parcel.priceDisplay || parcel.price || 'n/a')}</div>
                    </div>
                    
                    <div className="property-details">
                      <p><strong>{t('properties_size_label')}</strong> {parcel.size}</p>
                      <p><strong>{t('properties_location_label')}</strong> {parcel.location}</p>
                    </div>
                    
                    <div className="property-features">
                      <h4>{t('properties_key_features') || 'Key Features'}</h4>
                      <ul>
                        {(parcel.features || []).map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                    
                    <button 
                      className="view-on-map-btn"
                      onClick={() => onParcelSelect(parcel)}
                      disabled={isSold}
                    >
                      {isSold ? (t('properties_sold') || 'Sold') : (t('properties_view_on_map') || 'View on Map')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertiesTab;
